import { OpenAPIV3 } from 'openapi-types';
import * as OpenAPISampler from 'openapi-sampler';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import formUrlEncoded from 'form-urlencoded';
import { merge } from 'lodash';
import { sampleNexmoXmlSchema } from '@/utils/docs/api/sampleNexmoXmlSchema';

const SupportedContentTypes = [
  'multipart/form-data',
  'application/x-www-form-urlencoded',
  'application/json',
  'application/cloudevents+json',
  'application/problem+json',
  'application/hal+json',
  'text/xml',
  'application/xml',
];

export interface CodeSample {
  lang: string;
  code: string;
}

function mergeProperties(obj: JSONSchema7) {
  let properties: JSONSchema7 = {};
  let required: string[] = [];

  if (obj.allOf) {
    obj.allOf.forEach((subObj: JSONSchema7Definition) => {
      const { properties: subProperties, required: subRequired } = mergeProperties(subObj as JSONSchema7);
      properties = merge(properties, subProperties);
      required = [...required, ...subRequired];
    });
  }

  if (obj.properties) {
    properties = merge(properties, obj.properties);
  }

  if (obj.required) {
    required = [...required, ...obj.required];
  }

  return { properties, required };
}

export function mergeSchema(obj: JSONSchema7): JSONSchema7 {
  if (obj.items) {
    const items = obj.items as JSONSchema7;
    if (items.example !== undefined) {
      obj.example = [items.example];
      obj.items = mergeProperties(items) as JSONSchema7;
      return obj;
    }
    const selectedSchema = (items.oneOf?.[0] ?? items.anyOf?.[0]) as JSONSchema7 | undefined;

    if (selectedSchema) {
      if (selectedSchema.example !== undefined) {
        obj.example = [selectedSchema.example];
      }
      obj.items = mergeProperties(selectedSchema) as JSONSchema7;
      return obj;
    }

    obj.items = mergeProperties(items) as JSONSchema7;
    return obj;
  }

  return mergeProperties(obj) as JSONSchema7;
}

export function getSamplesForBodyType(
  schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject,
  skipNonRequired: boolean
): Record<string, unknown> {
  return OpenAPISampler.sample(mergeSchema(schema as JSONSchema7), { skipNonRequired }) as Record<string, unknown>;
}

export function getFormEncodedRequest(operationUrl: string, path: string, data: Record<string, unknown>): CodeSample {
  const urlEncoded = formUrlEncoded(data);

  // I think this makes the example incorrect however it has been requested to display
  // the values without the percent encoding. To resolve APIDP-1588 the decoding has been
  // limited to this type of example for now (previously it was done by the CodeSnippet component for all the
  // example types).
  const decodedValues = decodeURIComponent(urlEncoded);

  const url = new URL(operationUrl);
  return {
    lang: 'sh',
    code: [
      `POST ${url.pathname} HTTP/1.1`,
      `Host: ${url.host}`,
      `Content-Type: application/x-www-form-urlencoded`,
      `Content-Length: ${decodedValues.length}`,
      '',
      `${decodedValues}`,
    ].join('\n'),
  };
}

export function getJSONRequest(data: Record<string, unknown>): CodeSample {
  return {
    lang: 'json',
    code: JSON.stringify(data, null, 3),
  };
}

export function getXMLRequest(schema: OpenAPIV3.SchemaObject, skipNonRequired: boolean): CodeSample {
  return {
    lang: 'xml',
    code: sampleNexmoXmlSchema(schema, skipNonRequired),
  };
}

export const isContentTypeSupported = (type: string) => SupportedContentTypes.includes(type);

export function getCodeSamplesForSchema(
  schema: OpenAPIV3.SchemaObject,
  url: string,
  path: string,
  type: string,
  skipNonRequired: boolean
): CodeSample {
  const sample = getSamplesForBodyType(schema, skipNonRequired);
  switch (type) {
    case 'multipart/form-data':
    case 'application/x-www-form-urlencoded':
      return getFormEncodedRequest(url, path, sample);
    case 'application/json':
    case 'application/cloudevents+json':
    case 'application/problem+json':
    case 'application/hal+json':
      return getJSONRequest(sample);
    case 'text/xml':
    case 'application/xml':
      return getXMLRequest(schema, skipNonRequired);
    default:
      return { lang: 'text', code: 'Preview Not Available' };
  }
}
